export const normalizeLicensePlate = (value: string): string =>
  value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();

export const findDuplicateVehiclePlateIndex = (
  claimantPlate: string | undefined,
  liableVehicles: readonly { licensePlate?: string }[],
): number => {
  const normalizedClaimantPlate = claimantPlate && normalizeLicensePlate(claimantPlate);
  const plates = new Set(normalizedClaimantPlate ? [normalizedClaimantPlate] : []);

  return liableVehicles.findIndex(({ licensePlate }) => {
    if (!licensePlate) return false;

    const normalized = normalizeLicensePlate(licensePlate);
    if (!normalized) return false;
    if (plates.has(normalized)) return true;

    plates.add(normalized);
    return false;
  });
};

export const isValidLicensePlate = (value: string): boolean => {
  const normalized = normalizeLicensePlate(value);

  return /^[A-Z]{3}\d{4}$/.test(normalized) || /^[A-Z]{3}\d[A-Z]\d{2}$/.test(normalized);
};

const stripDigits = (value: string): string => value.replace(/\D/g, "");

const isRepeatedDigits = (digits: string): boolean => /^(\d)\1+$/.test(digits);

const cnhCheckDigit = (digits: string, weights: number[]): number => {
  const sum = weights.reduce((acc, weight, index) => acc + Number(digits[index]) * weight, 0);
  const remainder = sum % 11;

  return remainder >= 10 ? 0 : remainder;
};

export const isValidCnh = (value: string): boolean => {
  const digits = stripDigits(value);
  if (digits.length === 0) return true;
  if (digits.length !== 11 || isRepeatedDigits(digits)) return false;

  const firstCheckDigit = cnhCheckDigit(digits, [9, 8, 7, 6, 5, 4, 3, 2, 1]);
  const secondCheckDigit = cnhCheckDigit(digits, [1, 2, 3, 4, 5, 6, 7, 8, 9]);

  return firstCheckDigit === Number(digits[9]) && secondCheckDigit === Number(digits[10]);
};

export const isValidRg = (value: string): boolean => {
  const trimmed = value.trim().toUpperCase();
  if (trimmed.length === 0) return true;
  if (!/^[A-Z0-9][A-Z0-9 ./-]{2,18}[A-Z0-9]$/.test(trimmed)) {
    return false;
  }

  const alphanumeric = trimmed.replace(/[^A-Z0-9]/g, "");

  return alphanumeric.length >= 5 && /\d/.test(alphanumeric);
};
